import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function IncomeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    amount: '',
    created_at: new Date().toISOString().split('T')[0],
    purpose: '',
    member_id: '',
    fee_year: '',
    fee_semester: '',
  });
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchIncome();
    }
  }, [id]);

  const [nameSearchInput, setNameSearchInput] = useState('');

  const fetchMemberOptions = useCallback(async (searchTerm) => {
    setMemberSearchLoading(true);
    try {
      const res = await api.get('/members', {
        params: { search: searchTerm || undefined, limit: 50, status: 'all' },
      });
      setMemberOptions(res.data.data || []);
    } catch (err) {
      setMemberOptions([]);
    } finally {
      setMemberSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMemberOptions(nameSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [nameSearchInput, fetchMemberOptions]);

  const nameFieldValue = selectedMember || formData.name || null;

  const fetchCategories = async () => {
    try {
      const response = await api.get('/finance/income-categories');
      setCategories(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch categories');
    }
  };

  const fetchIncome = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/finance/incomes/${id}`);
      const income = response.data.data;
      setFormData({
        name: income.name || '',
        category_id: income.category_id || '',
        amount: income.amount || '',
        created_at: income.created_at ? income.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
        purpose: income.purpose || '',
        member_id: income.member_id || '',
        fee_year: income.fee_year || '',
        fee_semester: income.fee_semester || '',
      });
      if (income.member_id) {
        try {
          const memberRes = await api.get(`/members/${income.member_id}`);
          setSelectedMember(memberRes.data.data || null);
        } catch {
          setSelectedMember({ id: income.member_id, first_name: 'Member', last_name: income.member_id });
        }
      } else {
        setSelectedMember(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch income');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        category_id: parseInt(formData.category_id),
        member_id: formData.member_id || undefined,
        fee_year: formData.fee_year || undefined,
        fee_semester: formData.fee_semester || undefined,
      };

      if (id) {
        await api.put(`/finance/incomes/${id}`, data);
      } else {
        await api.post('/finance/incomes', data);
      }
      queryClient.invalidateQueries('dashboardMetrics');
      navigate('/dashboard/income');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save income');
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>{id ? 'Income Detail' : 'New Income'}</Box>
          </>
        }
        title={id ? 'Income Detail' : 'New Income'}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/income')}
            variant="outlined"
            sx={{ borderColor: tokens.border, color: tokens.ink }}
          >
            Incomes
          </Button>
        }
      />

      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={memberOptions}
                value={nameFieldValue}
                onInputChange={(e, newInput, reason) => {
                  if (reason === 'input') {
                    setNameSearchInput(newInput);
                    setSelectedMember(null);
                    setFormData((prev) => ({ ...prev, name: newInput, member_id: '' }));
                  }
                }}
                onChange={(e, newValue) => {
                  if (typeof newValue === 'object' && newValue !== null) {
                    setSelectedMember(newValue);
                    const fullName = `${newValue.first_name || ''} ${newValue.last_name || ''}`.trim() || newValue.email || 'Unknown';
                    setFormData((prev) => ({ ...prev, name: fullName, member_id: newValue.id }));
                  } else {
                    setSelectedMember(null);
                    setFormData((prev) => ({ ...prev, name: newValue || '', member_id: '' }));
                  }
                }}
                getOptionLabel={(opt) => {
                  if (typeof opt === 'string') return opt;
                  if (!opt) return '';
                  const name = `${opt.first_name || ''} ${opt.last_name || ''}`.trim();
                  const extra = opt.member_id ? ` (${opt.member_id})` : '';
                  return name + extra || opt.email || 'Unknown';
                }}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                loading={memberSearchLoading}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Name *"
                    required
                    placeholder="Search for a member or type name manually if not a member"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {memberSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {categories.find((c) => c.id === parseInt(formData.category_id, 10)) && /dues|membership|fee/i.test((categories.find((c) => c.id === parseInt(formData.category_id, 10)) || {}).name || '')
                  ? 'For membership fees, select a member and choose year/semester below.'
                  : 'Search and select an existing member, or type the name manually for non-members.'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category*</InputLabel>
                <Select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  label="Category*"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount (₵ Ghana Cedis)"
                name="amount"
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                value={formData.amount}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Created At"
                name="created_at"
                type="date"
                value={formData.created_at}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {(() => {
              const selCat = categories.find((c) => c.id === parseInt(formData.category_id, 10));
              const isMembershipFee = selCat && /dues|membership|fee/i.test(selCat.name || '');
              if (!isMembershipFee) return null;
              const currentYear = new Date().getFullYear();
              const years = Array.from({ length: 8 }, (_, i) => currentYear - 3 + i);
              return (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Year</InputLabel>
                      <Select
                        name="fee_year"
                        value={formData.fee_year}
                        onChange={handleChange}
                        label="Year"
                      >
                        {years.map((y) => (
                          <MenuItem key={y} value={String(y)}>
                            {y}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Semester</InputLabel>
                      <Select
                        name="fee_semester"
                        value={formData.fee_semester}
                        onChange={handleChange}
                        label="Semester"
                      >
                        <MenuItem value="Semester 1">Semester 1</MenuItem>
                        <MenuItem value="Semester 2">Semester 2</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              );
            })()}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={(() => {
                const base = loading || !formData.name || !formData.category_id || !formData.amount;
                const selCat = categories.find((c) => c.id === parseInt(formData.category_id, 10));
                const isMembershipFee = selCat && /dues|membership|fee/i.test(selCat.name || '');
                const membershipMissing = isMembershipFee && (!formData.member_id || !formData.fee_year || !formData.fee_semester);
                return base || membershipMissing;
              })()}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/income')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
